import { Capacitor } from "@capacitor/core";
import {
  Analytics,
  getAnalytics,
  logEvent as webLogEvent,
  setUserId as webSetUserId,
  setUserProperties as webSetUserProperties,
} from "firebase/analytics";
import { app } from "./app.firebase";

// Dynamic import for Capacitor plugin to avoid bundling in web builds
let FirebaseAnalytics: any = null;
const loadCapacitorPlugin = async () => {
  if (!FirebaseAnalytics && (Capacitor.getPlatform() === "android" || Capacitor.getPlatform() === "ios")) {
    const module = await import("@capacitor-firebase/analytics");
    FirebaseAnalytics = module.FirebaseAnalytics;
  }
  return FirebaseAnalytics;
};

let webAnalytics: Analytics | null = null;

/**
 * Initialize Firebase Analytics for web platform.
 * Should be called after Firebase app initialization.
 */
function initializeWebAnalytics(): Analytics | null {
  if (typeof window === "undefined") {
    // Not in browser environment (SSR)
    return null;
  }

  try {
    if (!webAnalytics) {
      webAnalytics = getAnalytics(app);
    }
    return webAnalytics;
  } catch (error) {
    console.warn("Failed to initialize Firebase Analytics for web:", error);
    return null;
  }
}

/**
 * Log a custom event to Firebase Analytics.
 * Works in both web/PWA and native environments.
 *
 * @param name - Event name
 * @param params - Optional event parameters
 */
export async function logEvent(
  name: string,
  params?: Record<string, any>,
): Promise<void> {
  const platform = Capacitor.getPlatform();

  try {
    if (platform === "web") {
      const analytics = initializeWebAnalytics();
      if (analytics) {
        webLogEvent(analytics, name, params);
      } else {
        console.warn("Firebase Analytics not initialized for web");
      }
    } else if (platform === "android" || platform === "ios") {
      const plugin = await loadCapacitorPlugin();
      if (plugin) {
        await plugin.logEvent({
          name,
          params: params,
        });
      }
    } else {
      console.warn(`Analytics not supported on platform: ${platform}`);
    }
  } catch (error) {
    console.error("Failed to log analytics event:", error);
  }
}

/**
 * Set the user ID for Firebase Analytics.
 * Works in both web/PWA and native environments.
 *
 * @param userId - User ID to set, or null to clear
 */
export async function setUserId(userId: string | null): Promise<void> {
  const platform = Capacitor.getPlatform();

  try {
    if (platform === "web") {
      const analytics = initializeWebAnalytics();
      if (analytics) {
        webSetUserId(analytics, userId);
      } else {
        console.warn("Firebase Analytics not initialized for web");
      }
    } else if (platform === "android" || platform === "ios") {
      const plugin = await loadCapacitorPlugin();
      if (plugin) {
        await plugin.setUserId({
          userId: userId,
        });
      }
    } else {
      console.warn(`Analytics not supported on platform: ${platform}`);
    }
  } catch (error) {
    console.error("Failed to set analytics user ID:", error);
  }
}

/**
 * Set a user property for Firebase Analytics.
 * Works in both web/PWA and native environments.
 *
 * @param name - Property name
 * @param value - Property value
 */
export async function setUserProperty(
  name: string,
  value: string,
): Promise<void> {
  const platform = Capacitor.getPlatform();

  try {
    if (platform === "web") {
      const analytics = initializeWebAnalytics();
      if (analytics) {
        webSetUserProperties(analytics, {
          [name]: value,
        });
      } else {
        console.warn("Firebase Analytics not initialized for web");
      }
    } else if (platform === "android" || platform === "ios") {
      const plugin = await loadCapacitorPlugin();
      if (plugin) {
        await plugin.setUserProperty({
          key: name,
          value,
        });
      }
    } else {
      console.warn(`Analytics not supported on platform: ${platform}`);
    }
  } catch (error) {
    console.error("Failed to set analytics user property:", error);
  }
}

/**
 * Reset Firebase Analytics data.
 * Works in both web/PWA and native environments.
 * Note: For web, this clears user ID and properties (web SDK doesn't have a reset function).
 */
export async function resetAnalytics(): Promise<void> {
  const platform = Capacitor.getPlatform();

  try {
    if (platform === "web") {
      const analytics = initializeWebAnalytics();
      if (analytics) {
        // Web SDK doesn't have reset, so we clear user ID and properties
        webSetUserId(analytics, null);
        webSetUserProperties(analytics, {});
      } else {
        console.warn("Firebase Analytics not initialized for web");
      }
    } else if (platform === "android" || platform === "ios") {
      const plugin = await loadCapacitorPlugin();
      if (plugin) {
        await plugin.resetAnalyticsData();
      }
    } else {
      console.warn(`Analytics not supported on platform: ${platform}`);
    }
  } catch (error) {
    console.error("Failed to reset analytics:", error);
  }
}

// Initialize web analytics if we're in a browser environment
if (typeof window !== "undefined" && Capacitor.getPlatform() === "web") {
  initializeWebAnalytics();
}
